import type {
  AdminLearningContentRow,
  AdminLearningContentRowUpsert,
  AdminLearningContentSubCategory,
  ProgramFixedContentRowUpsert,
} from '../types/domain';
import {
  createWorkspaceLearningContentTemplate,
  getWorkspaceLearningContentRows,
  replaceWorkspaceLearningContentRows,
} from '../services/workspaceLearningContentService';
import {
  formatLearningTextForDisplay,
  getUnitTextAndNoteForDisplay,
  serializeProgramUnitCellFromLearningContentAdmin,
} from './programCellDisplayText';
import {
  MIN_DISPLAY_PROGRAM_CONTENT_ROW_ID,
  withMinimumProgramContentRowsForDisplay,
} from './programWorkspaceBaselineDigest';

/** 기본 학습내용 등록으로 프로그램 학습내용을 내보낼 때 출판사 열에 쓰는 고정 값 */
export const WORKSPACE_LEARNING_EXPORT_PUBLISHER = '내보내기';

/** 단원~준비물 구간에 값이 있어 기본 학습내용 로드를 막을 때 */
export const PROGRAM_IMPORT_LEARNING_CONTENT_BLOCKED_MESSAGE =
  '기본 학습내용을 로드할 테이블의 단원~준비물/범교과/메모/평가 열에 기존 데이터가 있습니다. 기본 학습내용을 로드하려면 기존 데이터를 모두 삭제 후 실행해 주세요';

export function programAlignedImportColumnsHaveData(
  rows: ReadonlyArray<
    Pick<
      ProgramFixedContentRowUpsert,
      | 'unit_text'
      | 'learning_text'
      | 'page_text'
      | 'assistant_page_text'
      | 'current_period_text'
      | 'total_period_text'
      | 'material_text'
      | 'record_cross_curricular_text'
      | 'record_memo_text'
      | 'record_evaluation_text'
    >
  >
): boolean {
  for (const row of rows) {
    const { text, note } = getUnitTextAndNoteForDisplay(String(row.unit_text ?? ''));
    if (text.trim() || note.trim()) return true;
    if (formatLearningTextForDisplay(String(row.learning_text ?? '')).trim()) return true;
    if (String(row.page_text ?? '').trim()) return true;
    if (String(row.assistant_page_text ?? '').trim()) return true;
    if (String(row.current_period_text ?? '').trim()) return true;
    if (String(row.total_period_text ?? '').trim()) return true;
    if (String(row.material_text ?? '').trim()) return true;
    if (String(row.record_cross_curricular_text ?? '').trim()) return true;
    if (String(row.record_memo_text ?? '').trim()) return true;
    if (String(row.record_evaluation_text ?? '').trim()) return true;
  }
  return false;
}

export type ProgramLearningImportSeedMeta = {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
};

function adminFieldsOntoProgramRow(
  base: ProgramFixedContentRowUpsert,
  adm: AdminLearningContentRow,
  orderIndex: number
): ProgramFixedContentRowUpsert {
  const clearVirtualId = (base.id ?? '') === MIN_DISPLAY_PROGRAM_CONTENT_ROW_ID;
  return {
    ...base,
    id: clearVirtualId ? undefined : base.id,
    order_index: orderIndex,
    /** 학습내용 편제명(track_text) → 프로그램 시간표 열 */
    timetable_text: String(adm.track_text ?? ''),
    unit_text: serializeProgramUnitCellFromLearningContentAdmin(
      adm.unit_text ?? '',
      adm.achievement_text ?? ''
    ),
    learning_text: adm.learning_text ?? '',
    page_text: adm.page_text ?? '',
    assistant_page_text: adm.assistant_page_text ?? '',
    current_period_text: adm.target_lesson_text ?? '',
    total_period_text: adm.total_lesson_text ?? '',
    material_text: adm.material_text ?? '',
    record_cross_curricular_text: adm.record_cross_curricular_text ?? '',
    record_memo_text: adm.record_memo_text ?? '',
    record_evaluation_text: adm.record_evaluation_text ?? '',
  };
}

/**
 * 현재 표에 맞춰 정렬된 프로그램 행에 기본 학습내용 행을 행 단위로 덮어쓴다.
 * 관리 행이 더 길면 맨 아래 행을 복제해 출처 없는 행으로 늘린다(일정 넘침과 동일).
 * 연간 일정이 없어 baseline이 비어 있어도 `seedMeta`가 있으면 UI와 같이 빈 1행을 시드한 뒤 학습내용을 채운다.
 */
export function mergeProgramBaselineWithAdminLearningContent(
  currentAligned: ProgramFixedContentRowUpsert[],
  adminRows: AdminLearningContentRow[],
  seedMeta?: ProgramLearningImportSeedMeta
): ProgramFixedContentRowUpsert[] {
  const sortedAdmin = [...adminRows].sort((a, b) => a.order_index - b.order_index);
  const baseRows =
    currentAligned.length > 0
      ? currentAligned
      : seedMeta && sortedAdmin.length > 0
        ? withMinimumProgramContentRowsForDisplay([], {
            workspaceId: seedMeta.workspaceId,
            gradeId: seedMeta.gradeId,
            academicYear: seedMeta.academicYear,
            termNumber: seedMeta.termNumber,
          })
        : currentAligned;
  const lastBase = baseRows[baseRows.length - 1];
  if (!lastBase) return [];
  const total = Math.max(baseRows.length, sortedAdmin.length);
  const out: ProgramFixedContentRowUpsert[] = [];
  for (let i = 0; i < total; i += 1) {
    const base = baseRows[i];
    const adm = sortedAdmin[i];
    if (base && adm) {
      out.push(adminFieldsOntoProgramRow(base, adm, i));
    } else if (base && !adm) {
      out.push({ ...base, order_index: i });
    } else if (!base && adm) {
      out.push({
        ...adminFieldsOntoProgramRow(lastBase, adm, i),
        id: undefined,
        source_annual_cell_key: null,
        event_name: null,
        record_class_text: '',
        bg_color_by_field: {},
      });
    }
  }
  return out;
}

function hasProgramLearningContentData(row: ProgramFixedContentRowUpsert): boolean {
  const unit = getUnitTextAndNoteForDisplay(String(row.unit_text ?? ''));
  if (unit.text.trim() || unit.note.trim()) return true;
  if (formatLearningTextForDisplay(String(row.learning_text ?? '')).trim()) return true;
  if (String(row.page_text ?? '').trim()) return true;
  if (String(row.assistant_page_text ?? '').trim()) return true;
  if (String(row.current_period_text ?? '').trim()) return true;
  if (String(row.total_period_text ?? '').trim()) return true;
  if (String(row.material_text ?? '').trim()) return true;
  if (String(row.record_cross_curricular_text ?? '').trim()) return true;
  if (String(row.record_memo_text ?? '').trim()) return true;
  if (String(row.record_evaluation_text ?? '').trim()) return true;
  return false;
}

export function buildAdminLearningRowsFromProgramContentRows(params: {
  rows: ProgramFixedContentRowUpsert[];
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
  publisher: string;
}): AdminLearningContentRowUpsert[] {
  const { rows, workspaceId, gradeNumber, subCategory, templateName, publisher } = params;
  const scopeTemplate = templateName.trim();
  const scopePublisher = publisher.trim();
  const out: AdminLearningContentRowUpsert[] = [];
  rows.forEach((row) => {
    if (!hasProgramLearningContentData(row)) return;
    const unit = getUnitTextAndNoteForDisplay(String(row.unit_text ?? ''));
    out.push({
      workspace_id: workspaceId,
      grade_number: gradeNumber,
      sub_category: subCategory,
      template_name: scopeTemplate,
      order_index: out.length,
      publisher: scopePublisher,
      track_text: String(row.timetable_text ?? '').trim(),
      seq_text: String(out.length + 1),
      unit_text: unit.text,
      learning_text: formatLearningTextForDisplay(String(row.learning_text ?? '')),
      page_text: String(row.page_text ?? '').trim(),
      assistant_page_text: String(row.assistant_page_text ?? '').trim(),
      target_lesson_text: String(row.current_period_text ?? '').trim(),
      total_lesson_text: String(row.total_period_text ?? '').trim(),
      material_text: String(row.material_text ?? '').trim(),
      achievement_text: unit.note,
      record_cross_curricular_text: String(row.record_cross_curricular_text ?? ''),
      record_memo_text: String(row.record_memo_text ?? ''),
      record_evaluation_text: String(row.record_evaluation_text ?? ''),
    });
  });
  return out;
}

function learningContentRowToUpsert(row: AdminLearningContentRow): AdminLearningContentRowUpsert {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    grade_number: row.grade_number,
    sub_category: row.sub_category,
    template_name: row.template_name,
    order_index: row.order_index,
    publisher: row.publisher ?? '',
    track_text: row.track_text ?? '',
    seq_text: row.seq_text ?? '',
    unit_text: row.unit_text ?? '',
    learning_text: row.learning_text ?? '',
    page_text: row.page_text ?? '',
    assistant_page_text: row.assistant_page_text ?? '',
    target_lesson_text: row.target_lesson_text ?? '',
    total_lesson_text: row.total_lesson_text ?? '',
    material_text: row.material_text ?? '',
    achievement_text: row.achievement_text ?? '',
    record_cross_curricular_text: row.record_cross_curricular_text ?? '',
    record_memo_text: row.record_memo_text ?? '',
    record_evaluation_text: row.record_evaluation_text ?? '',
  };
}

/**
 * 프로그램 표(단원~평가) → 기본 학습내용 등록 사용자 편제.
 * 동일 학년·학기·편제에 출판사「내보내기」 행이 있으면 제거 후 이번 내보내기로 대체한다.
 */
export async function exportProgramLearningToWorkspace(params: {
  workspaceId: string;
  gradeId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
  programRows: ProgramFixedContentRowUpsert[];
}): Promise<{ exportedRowCount: number; replacedPriorExport: boolean }> {
  const templateName = params.templateName.trim();
  const gradeId = params.gradeId.trim();
  if (!templateName) throw new Error('편제명이 없어 내보낼 수 없습니다.');
  if (!gradeId) throw new Error('클래스를 선택해 주세요.');

  const exportedRows = buildAdminLearningRowsFromProgramContentRows({
    rows: params.programRows,
    workspaceId: params.workspaceId,
    gradeNumber: params.gradeNumber,
    subCategory: params.subCategory,
    templateName,
    publisher: WORKSPACE_LEARNING_EXPORT_PUBLISHER,
  });
  if (!exportedRows.length) {
    throw new Error('내보낼 학습내용(단원~평가)이 없습니다.');
  }

  const existing = await getWorkspaceLearningContentRows(
    params.workspaceId,
    gradeId,
    params.gradeNumber,
    params.subCategory,
    templateName,
    { forceRefresh: true }
  );
  const replacedPriorExport = existing.some(
    (row) => (row.publisher ?? '').trim() === WORKSPACE_LEARNING_EXPORT_PUBLISHER
  );
  const keptRows = existing
    .filter((row) => (row.publisher ?? '').trim() !== WORKSPACE_LEARNING_EXPORT_PUBLISHER)
    .map(learningContentRowToUpsert);
  const mergedRows = [
    ...keptRows.map((row, idx) => ({ ...row, order_index: idx })),
    ...exportedRows.map((row, idx) => ({ ...row, order_index: keptRows.length + idx, id: undefined })),
  ];

  await createWorkspaceLearningContentTemplate({
    workspaceId: params.workspaceId,
    gradeId,
    gradeNumber: params.gradeNumber,
    subCategory: params.subCategory,
    templateName,
  });
  await replaceWorkspaceLearningContentRows(
    params.workspaceId,
    gradeId,
    params.gradeNumber,
    params.subCategory,
    templateName,
    mergedRows
  );

  return { exportedRowCount: exportedRows.length, replacedPriorExport };
}
